import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  PLATFORM_ID,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { Location } from '@freelancer/location';
import { Assets } from '@freelancer/ui/assets';
import {
  FreelancerBreakpoints,
  FreelancerMaxBreakpoint,
} from '@freelancer/ui/breakpoints';
// NOTE: need to import from helpers directly to avoid circular dependency
import { isImageFile } from '@freelancer/ui/helpers';
import {
  deviceSizes,
  PictureDisplay,
  PictureLoadMethod,
  PictureMetadata,
  PictureObjectFit,
  PictureObjectPosition,
  PreloadInfo,
} from './picture.types';

@Component({
  selector: 'fl-picture',
  template: `
    <picture
      #container
      class="PictureElement"
      [ngClass]="{ IsShown: !lazyLoad || hasLazyLoadSupport || withinView }"
      [attr.data-display]="display"
      [attr.data-bounded-height]="boundedHeight"
      [attr.data-aspect-ratio]="fixedAspectRatio"
    >
      <source
        *ngIf="largeDesktopPicSource"
        [media]="FreelancerBreakpoints.DESKTOP_LARGE"
        [srcset]="
          !lazyLoad || hasLazyLoadSupport || withinView
            ? largeDesktopPicSource
            : undefined
        "
      />
      <source
        *ngIf="desktopPicSource"
        [media]="FreelancerBreakpoints.DESKTOP_SMALL"
        [srcset]="
          !lazyLoad || hasLazyLoadSupport || withinView
            ? desktopPicSource
            : undefined
        "
      />
      <source
        *ngIf="tabletPicSource"
        [media]="FreelancerBreakpoints.TABLET"
        [srcset]="
          !lazyLoad || hasLazyLoadSupport || withinView
            ? tabletPicSource
            : undefined
        "
      />
      <source
        *ngIf="pictureSource"
        [srcset]="
          !lazyLoad || hasLazyLoadSupport || withinView
            ? pictureSource
            : undefined
        "
      />
      <img
        #nativeImage
        class="ImageElement"
        [attr.height]="height"
        [attr.width]="width"
        [attr.data-display]="display"
        [attr.data-full-width]="fullWidth"
        [attr.data-bounded-width]="boundedWidth"
        [attr.data-bounded-height]="boundedHeight"
        [attr.data-align-center]="alignCenter"
        [attr.data-object-fit]="objectFit"
        [style.object-position]="
          objectPosition
            ? objectPosition.horizontal + ' ' + objectPosition.vertical
            : ''
        "
        [attr.data-aspect-ratio]="fixedAspectRatio"
        [attr.loading]="lazyLoad ? 'lazy' : undefined"
        [src]="
          !lazyLoad || hasLazyLoadSupport || withinView
            ? legacyPictureSource
            : undefined
        "
        [alt]="alt"
        (load)="handleImageLoad()"
        (error)="handleImageError($event)"
      />
    </picture>
    <fl-bit
      *ngIf="pictureMetadata"
      itemscope
      itemtype="https://schema.org/ImageObject"
    >
      <meta itemprop="contentUrl" [content]="src" />
      <span itemprop="author" itemscope itemtype="https://schema.org/Person">
        <meta itemprop="name" [content]="pictureMetadata.author" />
      </span>
      <meta itemprop="name" [content]="pictureMetadata.name" />
    </fl-bit>
  `,
  styleUrls: ['./picture.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PictureComponent
  implements OnChanges, OnInit, AfterViewInit, OnDestroy {
  FreelancerBreakpoints = FreelancerBreakpoints;

  @Input() src: string;
  @Input() alt: string;

  /** Forces the image to always have 100% of the width of its parent,
   * no matter how big or small it is */
  @Input() fullWidth: boolean;

  /** This ensures that the image will not overflow to its parent container by adding a max-width 100%
   * This mostly useful if you want your image to adjust accordingly on smaller
   * screens while keeping its original width on bigger screens. */
  @Input() boundedWidth?: boolean;

  /** This ensures that the image will not overflow to its parent container's height */
  @Input() boundedHeight?: boolean;

  /** Center aligns the image by setting the horizontal margins to auto */
  @Input() alignCenter?: boolean;

  /** Change the object-fit of the img element inside the component */
  @Input() objectFit?: PictureObjectFit;

  /** Change the object-position of the img element inside the component
   *
   * Used to specify how to position the picture within the container
   * When using the object-fit property, images are positioned at the center of the container by default.
   */
  @Input() objectPosition?: PictureObjectPosition;

  /** Fallback image source if image doesn't load */
  @Input() fallbackSrc: string;

  /** LEGACY - DO NOT USE. We will start to handle image optimization inside the component itself */
  @Input() srcTablet?: string;
  /** LEGACY - DO NOT USE. We will start to handle image optimization inside the component itself */
  @Input() srcDesktop?: string;
  /** LEGACY - DO NOT USE. We will start to handle image optimization inside the component itself */
  @Input() srcLargeDesktop?: string;

  /** Source image width in px.
   *
   * Used with height to allow the browser to calculate the image aspect ratio
   * and reserve space for the image before it is downloaded.
   *
   * Only determines display width if not set with css. */
  @Input() width?: number;

  /** Source image height in px.
   *
   * Used with width to allow the browser to calculate the image aspect ratio
   * and reserve space for the image before it is downloaded.
   *
   * Only determines display height if not set with css. */
  @Input() height?: number;

  /** Forces image to follow our standard aspect ratio with an object fit cover */
  @HostBinding('attr.data-aspect-ratio')
  @Input()
  fixedAspectRatio = false;

  @HostBinding('attr.data-display')
  @Input()
  display: PictureDisplay;

  /** This variable defines how an image is to be loaded */
  @Input()
  loadMethod?: PictureLoadMethod;

  /** Metadata related to the picture. Used to create ImageObject metadata for the picture. */
  @Input() pictureMetadata?: PictureMetadata;

  @Output() imageLoaded = new EventEmitter<ElementRef<HTMLImageElement>>();
  @Output() onError = new EventEmitter<void>();

  pictureSource?: string;
  /** This is to get the largest source image to display for IE */
  legacyPictureSource?: string;

  tabletPicSource?: string;
  desktopPicSource?: string;
  largeDesktopPicSource?: string;

  /** This tracks the position of the image relative to the screen. This is needed as a fallback for browsers with no native lazy load support */
  private observer: IntersectionObserver;
  /** This marks that the image should be loaded */
  withinView = false;
  /** This will be used in the template to check if lazyLoad is enabled */
  lazyLoad = false;
  /** This is set to false in SSR */
  hasLazyLoadSupport = false;

  @ViewChild('container') container: ElementRef;
  @ViewChild('nativeImage') nativeImage: ElementRef<HTMLImageElement>;

  constructor(
    @Inject(PLATFORM_ID) private platformId: string,
    @Inject(DOCUMENT) private doc: Document,
    private assets: Assets,
    private changeDetector: ChangeDetectorRef,
    private location: Location,
  ) {}

  ngOnInit() {
    this.lazyLoad = this.loadMethod === PictureLoadMethod.LAZY;
    this.hasLazyLoadSupport = 'loading' in HTMLImageElement.prototype;

    if (this.pictureSource && this.loadMethod === PictureLoadMethod.PRELOAD) {
      const preloadInformation: PreloadInfo[] = [
        {
          src: this.pictureSource,
          mediaQuery: '(min-width:360px)',
        },
      ];

      if (this.tabletPicSource) {
        const preloadInfo = preloadInformation[preloadInformation.length - 1];
        preloadInfo.mediaQuery = `${preloadInfo.mediaQuery} and ${FreelancerMaxBreakpoint.TABLET}`;

        const tabletPreloadInfo = {
          src: this.tabletPicSource,
          mediaQuery: `${FreelancerBreakpoints.TABLET}`,
        };
        preloadInformation.push(tabletPreloadInfo);
      }

      if (this.desktopPicSource) {
        const preloadInfo = preloadInformation[preloadInformation.length - 1];
        preloadInfo.mediaQuery = `${preloadInfo.mediaQuery} and ${FreelancerMaxBreakpoint.DESKTOP_SMALL}`;

        const desktopPreloadInfo = {
          src: this.desktopPicSource,
          mediaQuery: `${FreelancerBreakpoints.DESKTOP_SMALL}`,
        };
        preloadInformation.push(desktopPreloadInfo);
      }

      if (this.largeDesktopPicSource) {
        const preloadInfo = preloadInformation[preloadInformation.length - 1];
        preloadInfo.mediaQuery = `${preloadInfo.mediaQuery} and ${FreelancerMaxBreakpoint.DESKTOP_LARGE}`;

        const largeDesktopPreloadInfo = {
          src: this.largeDesktopPicSource,
          mediaQuery: `${FreelancerBreakpoints.DESKTOP_LARGE}`,
        };
        preloadInformation.push(largeDesktopPreloadInfo);
      }

      if (preloadInformation.length > 1) {
        preloadInformation.forEach(preloadInfo => {
          this.addPreloadImageLink(preloadInfo.src, preloadInfo.mediaQuery);
        });
      } else {
        this.addPreloadImageLink(this.pictureSource);
      }
    }
  }

  addPreloadImageLink(src: string, mediaQuery?: string) {
    const preloadLink = this.doc.createElement('link');
    preloadLink.setAttribute('rel', 'preload');
    preloadLink.setAttribute('href', src);
    preloadLink.setAttribute('as', 'image');
    if (mediaQuery) {
      preloadLink.setAttribute('media', mediaQuery);
    }
    this.doc.head.appendChild(preloadLink);
  }

  ngAfterViewInit() {
    if (this.lazyLoad && !this.hasLazyLoadSupport) {
      const config = {
        rootMargin: '100%',
      };
      if (isPlatformBrowser(this.platformId)) {
        this.observer = new IntersectionObserver(entries => {
          entries.forEach(entry => {
            if (entry.intersectionRatio === 0) {
              return;
            }
            this.withinView = true;
            this.observer.unobserve(this.container.nativeElement);
            this.changeDetector.markForCheck();
          });
        }, config);
        this.observer.observe(this.container.nativeElement);
      }
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (
      'src' in changes ||
      'srcTablet' in changes ||
      'srcDesktop' in changes ||
      'srcLargeDesktop' in changes
    ) {
      this.pictureSource = this.src
        ? this.appendResizerParameters(this.src)
        : undefined;

      if (this.srcTablet) {
        this.tabletPicSource = this.srcTablet
          ? this.appendResizerParameters(this.srcTablet)
          : undefined;
      }

      if (this.srcDesktop) {
        this.desktopPicSource = this.srcDesktop
          ? this.appendResizerParameters(this.srcDesktop)
          : undefined;
      }

      if (this.srcLargeDesktop) {
        this.largeDesktopPicSource = this.srcLargeDesktop
          ? this.appendResizerParameters(this.srcLargeDesktop)
          : undefined;
      }

      this.legacyPictureSource =
        this.largeDesktopPicSource ??
        this.desktopPicSource ??
        this.tabletPicSource ??
        this.pictureSource;
    }
  }

  isRawImageData(source: string): boolean {
    return source?.startsWith('data:image/');
  }

  appendResizerParameters(picSource: string): string {
    const src = this.getSrc(picSource);

    if (this.isRawImageData(src) || isImageFile(picSource, ['svg'])) {
      return src;
    }

    const processedSrc = new URL(src, this.location.origin);
    if (!processedSrc) {
      return src;
    }

    // TODO: Remove once width is enforced (T257447)
    if (!this.width) {
      return src;
    }

    processedSrc.searchParams.append('image-optimizer', 'force');

    // TODO: Once we use the same layout types as Next.js, use either
    // generateSrcSetWithDeviceSizes or generateSrcSetWith2x depending on layout type
    const srcset = this.generateSrcSetWith2x(
      processedSrc.toString(),
      this.width,
    );

    return srcset;
  }

  handleImageLoad() {
    this.imageLoaded.emit(this.nativeImage);
  }

  // Used for the following layout types (acc to Next.js):
  //  - Responsive
  //  - Fill
  generateSrcSetWithDeviceSizes(url: string) {
    const urls: string[] = deviceSizes.map(size => {
      const processedUrl = new URL(url);
      processedUrl.searchParams.append('width', size.toString());
      return `${processedUrl.toString()} ${size.toString()}w`;
    });

    return urls.join(', ');
  }

  // Used for the following layout types (acc to Next.js):
  //  - Intristic (default)
  //  - Fixed
  generateSrcSetWith2x(url: string, width: number) {
    const urlForSize1x = new URL(url);
    urlForSize1x.searchParams.append('width', width.toString());
    const urlForSize2x = new URL(url);
    urlForSize2x.searchParams.append('width', (width * 2).toString());

    return `${urlForSize1x.toString()} 1x, ${urlForSize2x.toString()} 2x`;
  }

  handleImageError(event: Event) {
    // For lazy loaded images, the src is set to undefined
    // to prevent them from being downloaded. However,
    // this triggers an error in safari.
    const img = event.target as HTMLElement;
    if (img.getAttribute('src') === '') {
      return;
    }

    if (this.fallbackSrc) {
      this.pictureSource = this.assets.getUrl(this.fallbackSrc);
    }

    this.onError.emit();
  }

  private getSrc(src: string) {
    // Check if the src is http(s), data:image, /api or protocol relative.
    if (
      !src ||
      src.startsWith('http://') ||
      src.startsWith('https://') ||
      src.startsWith('data:image/') ||
      src.startsWith('/api') ||
      src.startsWith('//')
    ) {
      return src;
    }

    return this.assets.getUrl(src);
  }

  ngOnDestroy() {
    if (this.observer) {
      this.observer.unobserve(this.container.nativeElement);
    }
  }
}
